/**
 * use-landing-assets — 投放落地页素材（cover / banner / logo）直传与预览
 *
 * 上传走共享 `uploadImageFile`（multipart + authedFetch，不手注 Bearer —— 见该文件头）。
 * 预览走代理读：素材在 admin 私有桶里没有公开直链，必须鉴权后由 Worker 代回 bytes，
 * 故**不能**把 r2_key 拼成 <img src>，得先 authedFetch 成 blob 再转 objectURL。
 */

import { AdminApiError, type AdminErrorBody } from '~/api/errors';
import { authedFetch } from '~/api/session-refresh';
import { uploadImageFile } from '~/api/upload-image';
import { newRequestId } from '~/lib/request-id';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

export type LandingAssetKind = 'cover' | 'banner' | 'logo';

export const landingAssetsApi = {
  upload: (file: File, kind: LandingAssetKind): Promise<{ r2_key: string }> =>
    uploadImageFileWithKind(file, kind),

  /**
   * 拉素材 bytes 转 objectURL 供 <img> 用。调用方负责在替换 / 卸载时 revoke，
   * 否则每次重选都漏一个 blob URL。
   */
  async previewUrl(r2Key: string): Promise<string> {
    const res = await authedFetch(
      `${API_BASE}/api/v1/landing-assets/content?r2_key=${encodeURIComponent(r2Key)}`,
      { headers: { 'X-Request-Id': newRequestId() } },
    );
    if (!res.ok) {
      throw new AdminApiError(res.status, {
        code: 'NOT_FOUND',
        message: '素材不存在或已被清理',
        requestId: '',
      } satisfies AdminErrorBody);
    }
    return URL.createObjectURL(await res.blob());
  },
};

/**
 * `uploadImageFile` 的字段名写死 `file`，而本端点还要一个 `kind` 字段区分素材用途。
 * 不去改那个共享函数的签名（books / ops 两处在用），在这里单独拼一次 FormData 也只多几行——
 * 但**鉴权与错误处理仍复用它**，不自己手写 authedFetch + envelope 解包。
 */
async function uploadImageFileWithKind(
  file: File,
  kind: LandingAssetKind,
): Promise<{ r2_key: string }> {
  // 把 kind 编进查询串，body 仍是 uploadImageFile 认识的单字段 multipart
  return uploadImageFile<{ r2_key: string }>(
    `/api/v1/landing-assets?kind=${encodeURIComponent(kind)}`,
    file,
  );
}
